import React, { useEffect, useMemo, useState } from 'react';
import { Search, Upload, Download, Trash2, Plus, X, Save, ClipboardPaste } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const SENHA_EDITAR = 'APAGAR';

function pedirSenha(acao) {
  const senha = window.prompt(`Digite a senha para ${acao}:`);
  if (senha === null) return false;
  if (senha !== SENHA_EDITAR) {
    alert('Senha incorreta.');
    return false;
  }
  return true;
}

const COLAR_COLS = ['Código', 'Descrição', 'Aplicação', 'Tipo', 'Marca', 'Mínimo', 'Máximo'];

function splitLinha(linha) {
  if (linha.includes('\t')) return linha.split('\t');
  if (linha.includes(';')) return linha.split(';');
  return linha.split(',');
}

function parseColar(texto) {
  const linhas = String(texto || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((l) => l.replace(/\u00a0/g, ' '))
    .filter((l) => l.trim() !== '');
  if (!linhas.length) return [];

  const headerCells = splitLinha(linhas[0]).map((c) => c.trim().toLowerCase());
  const temCabecalho = headerCells.some((c) =>
    ['código', 'codigo', 'descrição', 'descricao', 'aplicação', 'aplicacao', 'marca'].includes(c)
  );

  const headerMap = {
    código: 'Código',
    codigo: 'Código',
    descrição: 'Descrição',
    descricao: 'Descrição',
    aplicação: 'Aplicação',
    aplicacao: 'Aplicação',
    tipo: 'Tipo',
    marca: 'Marca',
    'mínimo': 'Mínimo',
    minimo: 'Mínimo',
    'mín.': 'Mínimo',
    'máximo': 'Máximo',
    maximo: 'Máximo',
    'máx.': 'Máximo',
    'qtd.': 'Qtd.',
    quantidade: 'Qtd.',
    cadastro: 'Cadastro',
    'código planilha': 'Código Planilha',
    'código logix': 'Código Logix',
    'local instalação': 'Local instalação',
  };

  const start = temCabecalho ? 1 : 0;
  const itens = [];
  for (const linha of linhas.slice(start)) {
    const cells = splitLinha(linha);
    if (cells.every((c) => !String(c || '').trim())) continue;
    const item = {};
    if (temCabecalho) {
      headerCells.forEach((h, i) => {
        const key = headerMap[h];
        if (key) item[key] = String(cells[i] ?? '').trim();
      });
    } else {
      COLAR_COLS.forEach((key, i) => {
        item[key] = String(cells[i] ?? '').trim();
      });
    }
    if (item['Código']) itens.push(item);
  }
  return itens;
}

const FORM_VAZIO = {
  codigo: '',
  descricao: '',
  aplicacao: '',
  tipo: '',
  marca: '',
  minimo: '',
  maximo: '',
};

function Inventario() {
  const [itens, setItens] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [aplicacaoFiltro, setAplicacaoFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [importFeedback, setImportFeedback] = useState(null);
  const [showImportar, setShowImportar] = useState(false);
  const [textoColar, setTextoColar] = useState('');

  const fetchItens = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/inventario`);
      setItens(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      alert('Erro ao carregar inventário');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItens();
  }, []);

  const tipos = useMemo(() => {
    return [...new Set(itens.map((i) => (i.tipo || '').trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, 'pt-BR')
    );
  }, [itens]);

  const aplicacoes = useMemo(() => {
    return [...new Set(itens.map((i) => (i.aplicacao || '').trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, 'pt-BR')
    );
  }, [itens]);

  const filtrados = useMemo(() => {
    const termo = searchTerm.trim().toLowerCase();
    return itens.filter((item) => {
      if (tipoFiltro && (item.tipo || '') !== tipoFiltro) return false;
      if (aplicacaoFiltro && (item.aplicacao || '') !== aplicacaoFiltro) return false;
      if (!termo) return true;
      return [item.codigo, item.descricao, item.marca, item.aplicacao, item.tipo]
        .some((c) => String(c || '').toLowerCase().includes(termo));
    });
  }, [itens, searchTerm, tipoFiltro, aplicacaoFiltro]);

  const handleAbrirImportar = () => {
    if (!pedirSenha('importar planilha')) return;
    setTextoColar('');
    setShowImportar(true);
  };

  const handleImportar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!/\.(xls|xlsx)$/i.test(file.name || '')) {
      setImportFeedback({
        type: 'error',
        message: 'Use arquivo .xls ou .xlsx. Se for .ods, salve como Excel antes.',
      });
      return;
    }

    const data = new FormData();
    data.append('arquivo', file);
    setImportando(true);
    setImportFeedback({ type: 'info', message: `Importando "${file.name}"…` });

    try {
      const res = await axios.post(`${API_URL}/inventario/importar`, data, { timeout: 120000 });
      aplicarResultadoImportacao(res.data, 'Importação concluída.');
      setShowImportar(false);
      await fetchItens();
    } catch (err) {
      console.error(err);
      setImportFeedback({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Erro ao importar planilha.',
      });
    } finally {
      setImportando(false);
    }
  };

  const aplicarResultadoImportacao = (data, fallback) => {
    const msg = data?.message || fallback;
    const erros = data?.erros || [];
    const total = (data?.adicionados || 0) + (data?.atualizados || 0);
    setImportFeedback({
      type: total > 0 ? 'success' : 'warning',
      message: msg,
      erros,
    });
  };

  const handleAplicarColar = async () => {
    const itens = parseColar(textoColar);
    if (!itens.length) {
      setImportFeedback({
        type: 'warning',
        message: 'Nada reconhecido. Cole as células do Excel, de preferência com a linha de títulos (Código, Descrição…).',
      });
      return;
    }
    setImportando(true);
    setImportFeedback({ type: 'info', message: `Aplicando ${itens.length} linha(s)…` });
    try {
      const res = await axios.post(`${API_URL}/inventario/colar`, { itens }, { timeout: 120000 });
      aplicarResultadoImportacao(res.data, 'Importação concluída.');
      setShowImportar(false);
      setTextoColar('');
      await fetchItens();
    } catch (err) {
      console.error(err);
      setImportFeedback({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Erro ao colar planilha.',
      });
    } finally {
      setImportando(false);
    }
  };

  const handleExportar = async () => {
    try {
      const res = await axios.get(`${API_URL}/inventario/exportar-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventario_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      alert('Erro ao exportar inventário');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este item do inventário?')) return;
    if (!pedirSenha('apagar')) return;
    try {
      await axios.delete(`${API_URL}/inventario/${id}`);
      fetchItens();
    } catch (err) {
      console.error(err);
      alert('Erro ao remover item');
    }
  };

  const handleOpenNovo = () => {
    if (!pedirSenha('adicionar item')) return;
    setFormData(FORM_VAZIO);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSalvarNovo = async (e) => {
    e.preventDefault();
    if (!formData.codigo.trim()) {
      alert('Informe o código.');
      return;
    }
    if (!formData.descricao.trim()) {
      alert('Informe a descrição.');
      return;
    }
    try {
      setSalvando(true);
      await axios.post(`${API_URL}/inventario`, {
        codigo: formData.codigo.trim(),
        descricao: formData.descricao.trim(),
        aplicacao: formData.aplicacao.trim() || null,
        tipo: formData.tipo.trim() || null,
        marca: formData.marca.trim() || null,
        minimo: formData.minimo === '' ? null : formData.minimo,
        maximo: formData.maximo === '' ? null : formData.maximo,
      });
      setShowModal(false);
      setFormData(FORM_VAZIO);
      fetchItens();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao salvar item');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="p-4 md:p-6 flex flex-col flex-1 min-h-0">
      <div className="flex flex-wrap gap-3 mb-4 text-sm shrink-0">
        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700">
          {itens.length} itens
        </span>
        <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700">
          {filtrados.length} exibidos
        </span>
      </div>

      {importFeedback ? (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm shrink-0 ${
            importFeedback.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-900'
              : importFeedback.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-900'
                : importFeedback.type === 'warning'
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-blue-50 border-blue-200 text-blue-900'
          }`}
        >
          <div className="flex justify-between gap-3 items-start">
            <p>{importFeedback.message}</p>
            <button
              type="button"
              onClick={() => setImportFeedback(null)}
              className="text-xs font-semibold opacity-70 hover:opacity-100 shrink-0"
            >
              Fechar
            </button>
          </div>
          {importFeedback.erros?.length ? (
            <ul className="mt-2 list-disc list-inside text-xs opacity-90 max-h-32 overflow-auto">
              {importFeedback.erros.map((erro, i) => (
                <li key={i}>{erro}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col xl:flex-row gap-3 mb-4 shrink-0">
        <div className="flex-1 relative min-w-0">
          <input
            type="text"
            placeholder="Buscar código, descrição, marca..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-w-[180px]"
        >
          <option value="">Todos os tipos</option>
          {tipos.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={aplicacaoFiltro}
          onChange={(e) => setAplicacaoFiltro(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-w-[200px]"
        >
          <option value="">Todas as aplicações</option>
          {aplicacoes.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleOpenNovo}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
        >
          <Plus size={18} />
          Novo item
        </button>
        <button
          type="button"
          onClick={handleAbrirImportar}
          disabled={importando}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-60"
          title="Arquivo Excel ou colar células"
        >
          <Upload size={18} />
          {importando ? 'Importando…' : 'Importar planilha'}
        </button>
        <button
          type="button"
          onClick={handleExportar}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
        >
          <Download size={18} />
          Exportar
        </button>
      </div>

      <div className="overflow-auto flex-1 min-h-0">
        {loading ? (
          <div className="py-12 text-center text-gray-500">Carregando inventário...</div>
        ) : (
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Código</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Descrição</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Aplicação</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Tipo</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Marca</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Mín.</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Máx.</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtrados.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    Nenhum item encontrado
                  </td>
                </tr>
              ) : (
                filtrados.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <td className="px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">{item.codigo}</td>
                    <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-100">{item.descricao || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{item.aplicacao || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{item.tipo || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{item.marca || '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{item.minimo ?? '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{item.maximo ?? '—'}</td>
                    <td className="px-3 py-2 text-sm">
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        title="Apagar (pede senha)"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showImportar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="bg-purple-700 text-white px-5 py-3 rounded-t-lg flex justify-between items-center">
              <h2 className="text-lg font-bold">Importar planilha</h2>
              <button type="button" onClick={() => setShowImportar(false)} className="p-1 hover:bg-purple-800 rounded">
                <X size={22} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-sm font-semibold text-gray-800 mb-2">Arquivo Excel</p>
                <label className={`inline-flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg cursor-pointer hover:bg-purple-700 ${importando ? 'opacity-60 pointer-events-none' : ''}`}>
                  <Upload size={16} />
                  Escolher arquivo .xls ou .xlsx
                  <input
                    type="file"
                    accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleImportar}
                    className="hidden"
                    disabled={importando}
                  />
                </label>
              </div>
              <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">Ou colar do Excel</p>
                <p className="text-sm text-gray-600">
                  Selecione as células no Excel (de preferência com a linha de títulos) e cole abaixo.
                  O código que já existe é atualizado; o código novo é incluído.
                </p>
                <textarea
                  value={textoColar}
                  onChange={(e) => setTextoColar(e.target.value)}
                  rows={10}
                  placeholder={'Código\tDescrição\tAplicação\tTipo\tMarca\tMínimo\tMáximo'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                />
                <p className="text-sm text-gray-700">
                  {parseColar(textoColar).length} linha(s) reconhecida(s)
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAplicarColar}
                    disabled={importando || !textoColar.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2"
                  >
                    <ClipboardPaste size={16} />
                    {importando ? 'Aplicando…' : 'Aplicar colagem'}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowImportar(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="bg-blue-600 text-white px-5 py-3 rounded-t-lg flex justify-between items-center">
              <h2 className="text-lg font-bold">Novo item no inventário</h2>
              <button type="button" onClick={() => setShowModal(false)} className="p-1 hover:bg-blue-700 rounded">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={handleSalvarNovo} className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                <input
                  name="codigo"
                  value={formData.codigo}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                <input
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aplicação</label>
                  <input
                    name="aplicacao"
                    value={formData.aplicacao}
                    onChange={handleChange}
                    list="lista-aplicacoes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <datalist id="lista-aplicacoes">
                    {aplicacoes.map((a) => (
                      <option key={a} value={a} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <input
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    list="lista-tipos"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <datalist id="lista-tipos">
                    {tipos.map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                <input
                  name="marca"
                  value={formData.marca}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mínimo</label>
                  <input
                    name="minimo"
                    type="number"
                    value={formData.minimo}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máximo</label>
                  <input
                    name="maximo"
                    type="number"
                    value={formData.maximo}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-60"
                >
                  <Save size={18} />
                  {salvando ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Inventario;
